import React, { useState, useEffect } from "react";
import TransactionTable from "./TransactionTable";
import BarChart from "../../components/charts/BarChart";
import Tabs from "../../components/layout/Tabs";
import YearOnYearChart from "../../components/charts/YearOnYearChart";
import TransactionPatternsChart from "../../components/charts/TransactionPatternsChart";
import Filter from "../../components/layout/Filter";
import { getVolume } from "../../api/transactioApi/transactionsApi";

function formatLocalDate(date: Date, endOfDay = false): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return `${year}-${month}-${day}T${time}`;
}

const AnalyticsOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [barData, setBarData] = useState<{ label: string; value: number }[]>(
    [],
  );
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<"day" | "month">("month");
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: new Date(new Date().getFullYear(), 0, 1), // Jan 1 of current year
    endDate: new Date(),
  });

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "year", label: "Year on Year" },
    { id: "patterns", label: "Transaction Patterns" },
    { id: "details", label: "Transaction Details" },
  ];

  useEffect(() => {
    let mounted = true;
    async function loadVolume() {
      try {
        setVolumeLoading(true);
        setVolumeError(null);
        const start = formatLocalDate(dateRange.startDate, false);
        const end = formatLocalDate(dateRange.endDate, true);
        const res = await getVolume({
          start_date: start,
          end_date: end,
          granularity,
        });
        if (!mounted) return;
        setBarData(
          (res || []).map((p) => ({ label: p.label, value: p.transactions })),
        );
      } catch (err: any) {
        if (mounted)
          setVolumeError(err?.message || "Failed to load volume data.");
      } finally {
        if (mounted) setVolumeLoading(false);
      }
    }
    loadVolume();
    return () => {
      mounted = false;
    };
  }, [dateRange, granularity]);

  // Min-width keeps bars readable; parent div scrolls horizontally on small screens
  const chartMinWidth = Math.max(600, barData.length * 60);

  return (
    <div className="analytics-overview">
      <div className="tabs-controls-row">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id)}
        />
      </div>

      {activeTab !== "details" && (
        <>
          {activeTab === "year" ? (
            <YearOnYearChart />
          ) : activeTab === "patterns" ? (
            <TransactionPatternsChart />
          ) : (
            <div className="volume-card">
              <div className="volume-card-header">
                <h3>Transactional Volume</h3>
                <div className="volume-controls">
                  <div className="granularity-toggle">
                    <button
                      className={granularity === "day" ? "active" : ""}
                      onClick={() => setGranularity("day")}
                    >
                      Day
                    </button>
                    <button
                      className={granularity === "month" ? "active" : ""}
                      onClick={() => setGranularity("month")}
                    >
                      Month
                    </button>
                  </div>
                  <Filter
                    defaultStart={dateRange.startDate}
                    defaultEnd={dateRange.endDate}
                    onApply={(start, end) =>
                      setDateRange({ startDate: start, endDate: end })
                    }
                  />
                </div>
              </div>

              {volumeLoading ? (
                <div className="loader-container small">
                  <div className="loader" />
                </div>
              ) : volumeError ? (
                <div className="error-box">
                  <p>⚠️ {volumeError}</p>
                </div>
              ) : (
                <div className="volume-scroll-wrapper">
                  <div style={{ minWidth: chartMinWidth }}>
                    <BarChart data={barData} height={280} />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <TransactionTable />
    </div>
  );
};

export default AnalyticsOverview;
